// The route pattern: the one route dialect. Sightmap view routes and step route conditions read into it
// (docs/step-action/model.md).
import { z } from 'zod'
import { isRouteParam, pathSegments, renderRouteKey, routeKey, type CanonicalRoute } from '../../stable-surfaces/route-key.ts'

// `param`: any one segment, or the segment `params` names. `wildcard` `one`: any one segment; `any`: one or more.
export const ROUTE_SEGMENT = z.union([
  z.object({ text: z.string() }).strict(),
  z.object({ param: z.string().min(1) }).strict(),
  z.object({ wildcard: z.enum(['one', 'any']) }).strict(),
])

export const ROUTE_PATTERN = z.array(ROUTE_SEGMENT)

export type RouteSegment = z.infer<typeof ROUTE_SEGMENT>
export type RoutePattern = z.infer<typeof ROUTE_PATTERN>

/** A stored route string (`/:owner/:repo/pulls`, `/docs/**`): `:name` → param, `*` → one, `**` → any, else text. */
export function readRoutePattern(route: string): RoutePattern {
  return pathSegments(route).map((segment): RouteSegment =>
    segment === '**' ? { wildcard: 'any' }
      : segment === '*' ? { wildcard: 'one' }
        : segment.length > 1 && segment.startsWith(':') ? { param: segment.slice(1) }
          : { text: segment })
}

/** The route string of a pattern, each param `params` holds as its value; without them `readRoutePattern` reads it back. */
export function renderRoutePattern(pattern: RoutePattern, params: ReadonlyMap<string, string> = new Map()): string {
  return `/${pattern.map(segment => 'text' in segment ? segment.text
    : 'param' in segment ? params.get(segment.param) ?? `:${segment.param}` : segment.wildcard === 'one' ? '*' : '**').join('/')}`
}

/** Whether `pattern` covers the whole path of `url`. A param `params` holds matches only its value. */
export function routeMatches(pattern: RoutePattern, url: URL, params: ReadonlyMap<string, string> = new Map()): boolean {
  return covers(pattern, pathSegments(url.pathname), (segment, value) =>
    'text' in segment ? segment.text === value : 'param' in segment ? (params.get(segment.param) ?? value) === value : true)
}

/** Whether a stored route covers a canonical route, both in canonical form: a param stands for a per-record id
 *  (`routeKey`), so it covers only an id segment; wildcards cover any segments. */
export function routeCoversCanonical(route: string, canonical: CanonicalRoute): boolean {
  return covers(readRoutePattern(renderRouteKey(routeKey(route))), routeKey(canonical).path, (segment, value) =>
    'text' in segment ? !isRouteParam(value) && value === segment.text : 'param' in segment ? isRouteParam(value) : true)
}

// Whether `pattern` covers all of `path`; `holds` decides a single-segment pattern segment against one path segment.
function covers<S>(pattern: RoutePattern, path: readonly S[], holds: (segment: RouteSegment, value: S) => boolean): boolean {
  const from = (p: number, s: number): boolean => {
    if (p === pattern.length) return s === path.length
    if (s === path.length) return false
    const segment = pattern[p]!
    if ('wildcard' in segment && segment.wildcard === 'any') {
      for (let next = s + 1; next <= path.length; next++) if (from(p + 1, next)) return true
      return false
    }
    return holds(segment, path[s]!) && from(p + 1, s + 1)
  }
  return from(0, 0)
}
